/**
 * The block marks: what a block is, and the way into what you can do with it.
 *
 * Drawn rather than lettered because they have to read at 14px inline and at
 * 20px when a block is folded down to its icon alone — a size where `⚗` is a
 * blob and a letter says nothing about what is inside.
 *
 * Both are built on a 16-unit grid and inherit `currentColor`, so a block's own
 * colour carries into its mark.
 */

/**
 * Two reactants becoming one product: ●● → ●
 *
 * The arrow is the universal mark for a reaction, and "two things converge into
 * a different thing" is the whole of what the block reports. The product is
 * drawn larger and filled so the eye lands on the result rather than the
 * ingredients — which is the same order the card itself reads in.
 */
export function ReactionIcon(): React.JSX.Element {
    return (
        <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
            {/* The two reactants, stacked so they fit the width an arrow needs. */}
            <circle cx="2.6" cy="4.2" r="2.1" fill="currentColor" opacity="0.55" />
            <circle cx="2.6" cy="11.8" r="2.1" fill="currentColor" opacity="0.55" />
            {/* Converging into the arrow — curves rather than straight lines, so
                at 16px they read as flow instead of as a bracket. */}
            <path
                d="M5 4.6 C7.4 5.6 7.6 6.6 8.2 8 M5 11.4 C7.4 10.4 7.6 9.4 8.2 8"
                fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.75"
            />
            {/* The arrowhead, pointing at the product. */}
            <path d="M8.2 8 L10.6 8 M9.4 6.6 L10.9 8 L9.4 9.4"
                fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
            {/* The product — bigger and solid: this is the answer. */}
            <circle cx="13.4" cy="8" r="2.4" fill="currentColor" />
        </svg>
    );
}

/**
 * What is in the vessel: a filled level inside an outline.
 *
 * The contents block answers "what did I put in", so its mark is the vessel
 * with something in it rather than an abstract `i` — it says which block this
 * is at a glance, beside a reaction mark that is unmistakably different.
 */
export function ContentsIcon(): React.JSX.Element {
    return (
        <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
            {/* The vessel: a beaker, squared off so it cannot be mistaken for the
                reaction mark's circles at small sizes. */}
            <path
                d="M4 2.5 v9 a2.6 2.6 0 0 0 2.6 2.6 h2.8 A2.6 2.6 0 0 0 12 11.5 v-9"
                fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
            />
            <path d="M3.1 2.5 h9.8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            {/* What is in it. */}
            <path
                d="M4.65 8 v3.5 a1.95 1.95 0 0 0 1.95 1.95 h2.8 A1.95 1.95 0 0 0 11.35 11.5 V8 Z"
                fill="currentColor" opacity="0.55"
            />
        </svg>
    );
}

/**
 * The way into a vessel's menu: three dots, stacked.
 *
 * Stacked, not in a row. On a line of text a row of three dots is an
 * ellipsis — the caption it sits beside is often a wrapped, clipped mixture
 * name, and the mark read as "…and the rest of the words". Vertical, it can
 * only be the menu affordance it is everywhere else.
 */
export function MenuIcon(): React.JSX.Element {
    return (
        <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
            <circle cx="8" cy="3.4" r="1.45" fill="currentColor" />
            <circle cx="8" cy="8" r="1.45" fill="currentColor" />
            <circle cx="8" cy="12.6" r="1.45" fill="currentColor" />
        </svg>
    );
}
